import { updateUserAddressSchema } from '../shared/validations/userValidations';
import { ValidatedForm, FormSubmit, FormInput } from './common/ValidatedForm';
import { useAuth } from '../utils/AuthContext.js';
import { getInfoForUser } from './common/util.js';
import { InfoIsFromEmployerMessage } from './common/CommonComponents.js';
import { useEffect, useRef, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useClientParams } from '../utils/AppConfigContext.js';
import { useLanguage } from '../utils/LanguageContext';
import translations from '../components/ComponentsTranslations.lang.json';
import { Alert } from 'antd';

const googleMaps = {
  startLoading: () => (window.googleMapsIsLoading = true),
  isLoading: () => window.googleMapsIsLoading === true,
  finishLoading: () => (window.googleMapsIsLoaded = true),
  finishedLoading: () => window.googleMapsIsLoaded === true,
  markAvailable: () => (window.googleMapsIsAvailable = true),
  isAvailable: () => window.googleMapsIsAvailable === true,
};

const AddressFields = ({ prefix = 'address' }) => {
  const { googleMapsApiKey } = useClientParams();
  const { setValue } = useFormContext();

  const nameFor = useCallback((field) => `${prefix}.${field}`, [prefix]);
  const streetInputRef = useRef(null);
  const unitInputRef = useRef(null);

  useEffect(() => {
    const setValueFor = (field, value) => setValue(nameFor(field), value);

    const loadGoogleMapsScript = async () => {
      if (googleMaps.isLoading()) {
        return;
      }
      googleMaps.startLoading();

      if (!googleMapsApiKey) {
        googleMaps.finishLoading();
      } else {
        const script = document.createElement('script');
        script.id = 'google-maps-script';
        script.async = true;
        script.defer = true;
        script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places&loading=async&callback=initializeGoogleMaps`;
        script.onload = () => {
          window.initializeGoogleMaps = () => {
            googleMaps.finishLoading();
            googleMaps.markAvailable();

            initializeAutocomplete();
          };
        };
        document.head.appendChild(script);
      }
    };

    const initializeAutocomplete = () => {
      if (!(googleMaps.isAvailable() && streetInputRef.current)) {
        return;
      }

      const autocomplete = new window.google.maps.places.Autocomplete(streetInputRef.current.nativeElement, {
        componentRestrictions: { country: ['us'] },
        fields: ['address_components'],
        types: ['address'],
      });

      autocomplete.addListener('place_changed', () => {
        const { address_components } = autocomplete.getPlace();

        if (address_components) {
          const find = (name) => address_components.find((c) => c.types[0] === name) ?? {};
          const long = (name) => find(name).long_name;
          const short = (name) => find(name).short_name;

          setValueFor('street', (long('street_number') + ' ' + short('route')).trim());

          //need both to clear it out
          setValueFor('unit', null);

          setValueFor('city', long('locality') || long('sublocality_level_1'));
          setValueFor('state', short('administrative_area_level_1'));
          setValueFor('zip', long('postal_code'));
          setValueFor('country', short('country') === 'US' ? 'USA' : short('country'));

          if (unitInputRef.current) unitInputRef.current.focus();
        }
      });
    };

    if (googleMaps.finishedLoading()) {
      initializeAutocomplete();
    } else {
      loadGoogleMapsScript();
    }
  }, [googleMapsApiKey, setValue, nameFor]);

  return (
    <>
      <FormInput name={nameFor('street')} type="text" ref={streetInputRef} />
      <FormInput name={nameFor('unit')} type="text" ref={unitInputRef} />
      <FormInput name={nameFor('city')} type="text" />
      <FormInput name={nameFor('state')} type="text" />
      <FormInput name={nameFor('zip')} type="text" />
      <FormInput name={nameFor('country')} type="text" />
    </>
  );
};

const AddressForm = ({ onFinish, submitLabel }) => {
  const { lang } = useLanguage(translations);
  const { user, userEmployments, api } = useAuth();
  const { address, infoComesFromEmployer } = getInfoForUser(
    user,
    userEmployments,
    (v) => v.address && v.address.street,
  );

  return (
    <ValidatedForm
      key="userAddress"
      schema={updateUserAddressSchema}
      initialValues={{ address }}
      handleSubmit={api.updateUserAddress}
      onFinish={onFinish}
    >
      {infoComesFromEmployer && <InfoIsFromEmployerMessage />}
      <AddressFields />
      <Alert type="info" message={lang('taxResidencyRequiredToBeInUSA')} />
      <FormSubmit>{submitLabel}</FormSubmit>
    </ValidatedForm>
  );
};

export { AddressFields };
export default AddressForm;
